import { getCurrentInstance, computed } from "vue";
import { apolloClient } from "@/plugins/apollo";
import gql from "fraql";
import { useTranslation } from "@/plugins/i18next/i18next";
import { competitionOrderCompareMethod } from "@/utils/competition_utils";
export function useCompetitionHelpers() {
    const instance = getCurrentInstance().proxy;
    const { t } = useTranslation();
    const competitionOrderFn = computed(() => {
        return competitionOrderCompareMethod(instance.$federation.language);
    });
    function competitionsOrdered(competitions) {
        if (!Array.isArray(competitions))
            return [];
        return competitions.sort((a, b) => competitionOrderFn.value(a.code, b.code));
    }
    function isRestricted(c) {
        var _a;
        if (c.isRestricted) {
            let txt = "";
            if (((_a = c.competitionRule) === null || _a === void 0 ? void 0 : _a.description) || c.description) {
                txt = " | ";
            }
            return (txt +
                `${t("events:schedule.classes.rankingsParameters.restricted.text", "Restricted")}`);
        }
    }
    function isTeamCompetition(c) {
        var _a;
        if (c.isTeamCompetition) {
            let txt = "";
            if (isRestricted(c) || ((_a = c.competitionRule) === null || _a === void 0 ? void 0 : _a.description) || c.description) {
                txt = " | ";
            }
            return (txt +
                `${t("events:schedule.classes.rankingsParameters.team.text", "Team")}`);
        }
    }
    function outsideOfCompetitionAllowed(c) {
        var _a;
        if (c.outsideOfCompetitionAllowed) {
            let txt = "";
            if (isTeamCompetition(c) ||
                isRestricted(c) ||
                ((_a = c.competitionRule) === null || _a === void 0 ? void 0 : _a.description) ||
                c.description) {
                txt = " | ";
            }
            return (txt +
                `${t("events:common.outsideOfCompetitionAllowed.text", "out of competition allowed")}`);
        }
    }
    function isIndoor(c) {
        if (c.isIndoor) {
            return `${c.competitionRuleId ? " | " : ""}${t("events:schedule.classes.rankingsParameters.indoor.text", "Indoor")}`;
        }
    }
    return {
        competitionOrderFn,
        competitionsOrdered,
        isRestricted,
        outsideOfCompetitionAllowed,
        isTeamCompetition,
        isIndoor
    };
}
export function useCompetitionMutationHelpers() {
    const instance = getCurrentInstance().proxy;
    const { t } = useTranslation();
    async function cloneCompetitionMutation(competitionId, queryOptions = { refetchQueries: [] }) {
        // @ts-ignore
        const response = await instance.$root.$confirm(t("events:competion.cloneCompetition.heading"), t("events:competion.cloneCompetition.text"), {
            confirmText: t("common:button.confirm")
        });
        if (!response)
            return Promise.resolve({ data: null });
        const data = await apolloClient.mutate({
            mutation: gql `
        mutation eventCompetitionClone($id: ID!) {
          eventCompetitionClone(input: { id: $id }) {
            competition {
              id
            }
          }
        }
      `,
            ...queryOptions,
            variables: {
                id: competitionId
            }
        });
        return Promise.resolve(data);
    }
    async function deleteCompetitionMutation(competitionId, queryOptions = { refetchQueries: [] }) {
        // @ts-ignore
        const response = await instance.$root.$confirm(t("events:schedule.Actions.deleteCompetition.heading", "Delete competition"), t("events:schedule.Actions.deleteCompetition.question.text", "Are you sure?"));
        if (!response)
            return Promise.resolve({ data: null });
        const data = await apolloClient.mutate({
            mutation: gql `
        mutation eventCompetitionDelete($id: ID!) {
          eventCompetitionDelete(input: { id: $id }) {
            event {
              id
              competitions {
                id
              }
            }
          }
        }
      `,
            ...queryOptions,
            variables: { id: competitionId }
        });
        return Promise.resolve(data);
    }
    return {
        cloneCompetitionMutation,
        deleteCompetitionMutation
    };
}
